import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  useTheme,
  alpha,
  Card,
  CardContent,
  Chip,
  Stack,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import companyService, { Company } from '../../services/company.service';

interface CompanyFormProps {
  isEditMode?: boolean;
}

const CompanyForm: React.FC<CompanyFormProps> = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();

  // Form state
  const [formData, setFormData] = useState<Company>({
    name: '',
    email_id: '',
    contact_no: '',
    country_code: '',
    is_email_verify: false,
    website: '',
    address: '',
    industry: '',
    about_us: '',
    contactPerson: '',
    founded_date: undefined,
    video_link: '',
    social_media_profiles: [],
    company_size: undefined,
    policy: '',
    is_deleted: false,
    status: 'active',
  });

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchLoading, setFetchLoading] = useState<boolean>(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Social Media state
  const [socialPlatform, setSocialPlatform] = useState<string>('');
  const [socialLink, setSocialLink] = useState<string>('');

  // Fetch company data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchCompany = async () => {
        setFetchLoading(true);
        try {
          const company = await companyService.getCompany(id);
          setFormData(company);
        } catch (err: any) {
          setError(err?.response?.data?.message || 'Failed to fetch company details');
          console.error('Error fetching company:', err);
        } finally {
          setFetchLoading(false);
        }
      };

      fetchCompany();
    }
  }, [isEditMode, id]);

  // Handle form input changes
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  
  // Add social media profile
  const handleAddSocialMedia = () => {
    if (socialPlatform && socialLink) {
      setFormData(prev => ({
        ...prev,
        social_media_profiles: [
          ...(prev.social_media_profiles || []),
          {
            platform: socialPlatform,
            link: socialLink
          }
        ]
      }));
      
      // Reset fields
      setSocialPlatform('');
      setSocialLink('');
    }
  };
  
  // Remove social media profile
  const handleRemoveSocialMedia = (index: number) => {
    setFormData(prev => ({
      ...prev,
      social_media_profiles: prev.social_media_profiles?.filter((_, i) => i !== index) || []
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isEditMode && id) {
        await companyService.updateCompany(id, formData);
        setSuccess('Company updated successfully');
      } else {
        await companyService.createCompany(formData);
        setSuccess('Company created successfully');
        
        // Reset form after successful creation
        if (!isEditMode) {
          setFormData({
            name: '',
            email_id: '',
            contact_no: '',
            country_code: '',
            is_email_verify: false,
            website: '',
            address: '',
            industry: '',
            about_us: '',
            contactPerson: '',
            founded_date: undefined,
            video_link: '',
            social_media_profiles: [],
            company_size: undefined,
            policy: '',
            is_deleted: false,
            status: 'active',
          });
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save company');
      console.error('Error saving company:', err);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/companies')} 
          sx={{ mr: 1 }}
          aria-label="Back to companies"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" fontWeight="bold">
          {isEditMode ? 'Edit Company' : 'Add New Company'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper
        elevation={3}
        component="form"
        onSubmit={handleSubmit}
        sx={{ p: 3, borderRadius: 2 }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              id="name"
              name="name"
              label="Company Name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              id="email_id"
              name="email_id"
              label="Email"
              type="email"
              value={formData.email_id}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              id="country_code"
              name="country_code"
              label="Country Code"
              value={formData.country_code || ''}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              required
              fullWidth
              id="contact_no"
              name="contact_no"
              label="Phone"
              value={formData.contact_no}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="website"
              name="website"
              label="Website"
              value={formData.website || ''}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="address"
              name="address"
              label="Address"
              value={formData.address || ''}
              onChange={handleChange}
              disabled={loading}
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Social Media Profiles</Typography>
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Platform"
                    value={socialPlatform}
                    onChange={(e) => setSocialPlatform(e.target.value)}
                    placeholder="e.g. LinkedIn, Twitter"
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Link"
                    value={socialLink}
                    onChange={(e) => setSocialLink(e.target.value)}
                    placeholder="https://..."
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddSocialMedia}
                    disabled={!socialPlatform || !socialLink || loading}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Box>
            
            {formData.social_media_profiles && formData.social_media_profiles.length > 0 && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {formData.social_media_profiles.map((profile, index) => (
                      <Chip
                        key={index}
                        label={`${profile.platform}: ${profile.link}`}
                        onDelete={() => handleRemoveSocialMedia(index)}
                        disabled={loading}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="industry"
              name="industry"
              label="Industry"
              value={formData.industry || ''}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="contactPerson"
              name="contactPerson"
              label="Contact Person"
              value={formData.contactPerson || ''}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status"
                name="status"
                value={formData.status}
                label="Status"
                onChange={handleChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="about_us"
              name="about_us"
              label="About Us"
              value={formData.about_us || ''}
              onChange={handleChange}
              disabled={loading}
              multiline
              rows={3}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="founded_date"
              name="founded_date"
              label="Founded Date"
              type="date"
              value={formData.founded_date ? new Date(formData.founded_date).toISOString().split('T')[0] : ''}
              onChange={handleChange}
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="video_link"
              name="video_link"
              label="Video Link"
              value={formData.video_link || ''}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel id="company-size-label">Company Size</InputLabel>
              <Select
                labelId="company-size-label"
                id="company_size"
                name="company_size"
                value={formData.company_size || ''}
                label="Company Size"
                onChange={handleChange}
              >
                <MenuItem value="1-50">1-50</MenuItem>
                <MenuItem value="51-100">51-100</MenuItem>
                <MenuItem value="101-500">101-500</MenuItem>
                <MenuItem value="501-1000">501-1000</MenuItem>
                <MenuItem value="1001-5000">1001-5000</MenuItem>
                <MenuItem value="5001-10000">5001-10000</MenuItem>
                <MenuItem value="10001+">10001+</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel id="is-email-verify-label">Email Verified</InputLabel>
              <Select
                labelId="is-email-verify-label"
                id="is_email_verify"
                name="is_email_verify"
                value={formData.is_email_verify === true ? 'true' : 'false'}
                label="Email Verified"
                onChange={e => {
                  setFormData(prev => ({
                    ...prev,
                    is_email_verify: e.target.value === 'true'
                  }));
                }}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="policy"
              name="policy"
              label="Policy"
              value={formData.policy || ''}
              onChange={handleChange}
              disabled={loading}
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/companies')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ 
                minWidth: 120,
                boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              {loading ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Save'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CompanyForm;